/**
 * PageHeader — the locked per-page header block (composed from DS components, not bespoke):
 * mandatory SkyBreadcrumbTrail -> SkyDivider -> exactly ONE SkyTitle1 (as h1) -> optional description.
 * Non-current crumbs navigate via react-router (onClick + useNavigate) so there is no full-page reload;
 * the last crumb is the current page (no link). This keeps "exactly one H1 per page" trivially true.
 */
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  SkyBreadcrumbTrail,
  SkyDivider,
  SkyTitle1,
  SkyText,
  type BreadcrumbItemData,
} from "@skypointcloud/skypoint-ui";

export interface Crumb {
  key: string;
  label: string;
  /** Router path; when set (and not the current page) the crumb navigates client-side. */
  to?: string;
}

export function PageHeader({
  crumbs,
  title,
  description,
  headerAction,
}: {
  crumbs: Crumb[];
  title: string;
  description?: ReactNode;
  /**
   * M2(a): an OPTIONAL right-aligned primary action rendered on the H1 row (skySign page-pattern
   * parity). Use ONLY where a real action exists — never invent a fake CTA to fill the slot.
   */
  headerAction?: ReactNode;
}) {
  const navigate = useNavigate();
  const items: BreadcrumbItemData[] = crumbs.map((c) => ({
    key: c.key,
    label: c.label,
    onClick: c.to ? () => navigate(c.to as string) : undefined,
  }));

  return (
    <div className="flex flex-col">
      <SkyBreadcrumbTrail items={items} />
      <div className="mt-1">
        <SkyDivider />
      </div>
      <div className="mt-4 flex items-start justify-between gap-4">
        <div>
          <SkyTitle1 as="h1">{title}</SkyTitle1>
          {description ? (
            <div className="mt-1">
              <SkyText size={300}>{description}</SkyText>
            </div>
          ) : null}
        </div>
        {headerAction ? <div className="shrink-0">{headerAction}</div> : null}
      </div>
    </div>
  );
}
